/**
 * TrailerSearchSelect Component
 * Takes user input and fetches matching trailers asynchronously.
 * @param {function} onSelect - Callback function when a trailer is selected
 * @param {object} selectedTrailer - Currently selected trailer object {value, label}
 * @param {boolean} enableAll - If true, enables all options regardless of location
 * @param {boolean} location - Includes location in the label if true
 * @param {boolean} required - If true, makes the selection required
 * @param {object} styles - Custom styles for the React Select component
 **/

import React, { useMemo } from "react";
import AsyncSelect from "react-select/async";
import instance from "../../Interceptor/api_instance";
import debounce from "lodash.debounce";
import { customStyles } from "../Utility/ReactSelectStyles";
import { useTranslation } from "react-i18next";

const TrailerSearchSelect = ({
  onSelect,
  selectedTrailer,
  enableAll = false,
  location = true,
  required = true,
  styles = customStyles
}) => {
  const { t } = useTranslation();
  const filterOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];

    try {
      const res = await instance.get(`assets/out-trailers?q=${inputValue}`);
      return res.data.map((trailer) => ({
        trailerRepairs: trailer.open_repairs || [],
        value: trailer.id,
        label: `${trailer.unit} ${
          location ? trailer.location?.loc_name || "" : ""
        }`,
        licensePlate: trailer.license_plate,
        isDisabled: trailer.location && !enableAll ? true : false,
      }));
    } catch (err) {
      console.error("Failed to fetch trailers: ", err);
      return [];
    }
  };

  // ✅ Debounce filterOptions and return a wrapped function that resolves a Promise
  const debouncedLoadOptions = useMemo(() => {
    const wrapped = debounce((inputValue, callback) => {
      filterOptions(inputValue).then(callback);
    }, 1000); // 500ms debounce

    return (inputValue) =>
      new Promise((resolve) => wrapped(inputValue, resolve));
  }, []);

  return (
    <AsyncSelect
      aria-label="Trailer"
      inputId="trailer-select"
      name="trailer"
      defaultOptions={false}
      loadingMessage={() => t("general.loading")}
      loadOptions={debouncedLoadOptions}
      onChange={(option) => onSelect(option)}
      // placeholder={t("forms.type.vehicle.search.trailer.search")}
      placeholder={"Trailer"}
      noOptionsMessage={() => t("forms.type.vehicle.search.trailer.not-found")}
      isClearable
      className="search-select"
      styles={styles}
      unstyled={true}
      value={selectedTrailer}
      required={required}
    />
  );
};

export default React.memo(TrailerSearchSelect);
